import { createPopper } from '@popperjs/core'

type CollapseItem = {
  priority: number
  label: string
  icon: string
  element: HTMLElement
}

up.compiler(
  '[data-collapse-menu--button]',
  (button: HTMLElement, { container: containerSelector }: { container: string }) => {
    const container = button.closest<HTMLElement>(containerSelector)!

    const menuList = up.element.affix(document.body, '.collapse-menu.-hidden')

    let popper: ReturnType<typeof createPopper> | undefined
    let isOpen = false
    let hiddenItems: CollapseItem[] = []

    function getSortedCollapsibles(container: HTMLElement): CollapseItem[] {
      return Array.from(
        container.querySelectorAll<HTMLElement>('[data-collapse-menu--collapsible]'),
      )
        .map((element) => {
          const { priority, label, icon } = JSON.parse(
            element.getAttribute('data-collapse-menu--collapsible')!,
          ) as Omit<CollapseItem, 'element'>
          return {
            element,
            priority,
            label,
            icon,
          }
        })
        .sort((a, b) => a.priority - b.priority)
    }

    function onButtonClick(event: Event) {
      event.preventDefault()
      event.stopPropagation()

      if (!hiddenItems.length) {
        return
      }

      toggleMenu()
    }

    const onDocumentClick = (event: Event) => {
      const target = event.target as Node
      if (!isOpen) return
      if (target && (menuList.contains(target) || button.contains(target))) return
      toggleMenu(false)
    }

    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        toggleMenu(false)
      }
    }

    function isOverflowing() {
      const visibleChildren = Array.from(container.children).filter((child) => {
        const element = child as HTMLElement
        return !element.hidden && element.getClientRects().length > 0
      }) as HTMLElement[]

      if (!visibleChildren.length) return false

      const containerRect = container.getBoundingClientRect()

      return visibleChildren.some((child) => {
        const rect = child.getBoundingClientRect()
        return rect.left < containerRect.left - 1 || rect.right > containerRect.right + 1
      })
    }

    function relayout() {
      hiddenItems = []

      collapsibles.forEach((item) => {
        item.element.hidden = false
      })

      button.classList.add('d-none')
      toggleMenu(false)

      if (!isOverflowing()) {
        return
      }

      button.classList.remove('d-none')

      for (const item of collapsibles) {
        if (!isOverflowing()) break
        item.element.hidden = true
        hiddenItems.push(item)
      }

      if (!hiddenItems.length) {
        button.classList.add('d-none')
      }
    }

    function toggleMenu(open: boolean = !isOpen) {
      if (!hiddenItems.length) return

      if (open) {
        updateMenu()
      }
      menuList.classList.toggle('-hidden', !open)
      isOpen = open
    }

    function updateMenu() {
      renderMenuItems()
      popper ??= createPopper(button, menuList, {
        placement: 'bottom-end',
        modifiers: [
          { name: 'offset', options: { offset: [0, 8] } },
          { name: 'preventOverflow', options: { padding: 8 } },
        ],
      })
      void popper.update()
    }

    function renderMenuItems() {
      menuList.innerHTML = ''

      for (const item of hiddenItems) {
        const link = up.element.affix(menuList, 'button[type=button].collapse-menu--item')
        up.element.affix(link, `.icon.-${item.icon}`)
        link.append(item.label)

        link.addEventListener('click', (event: Event) => {
          event.preventDefault()
          toggleMenu(false)

          const clickable = item.element.querySelector<HTMLElement>('a,button') || item.element
          clickable.click()
        })
      }
    }

    button.addEventListener('click', onButtonClick)
    document.addEventListener('click', onDocumentClick, true)
    document.addEventListener('keydown', onEscape)

    const collapsibles = getSortedCollapsibles(container)
    relayout()

    const resizeObserver = new ResizeObserver(() => relayout())
    resizeObserver.observe(container)

    const disconnectOnRelayout = up.on(container, 'app.relayout', relayout)

    return () => {
      resizeObserver.disconnect()
      disconnectOnRelayout()
      document.removeEventListener('click', onDocumentClick, true)
      document.removeEventListener('keydown', onEscape)
      popper?.destroy()
      menuList.remove()
      collapsibles.forEach((item) => {
        item.element.hidden = false
      })
    }
  },
)
